import distributionsApi from "./Api/distributionsApi";

export default function () {

  // Generic method for making API requests
  const makeRequest = async (url, method, payload = null, axiosConfig = {}) => {
    const axios = useNuxtApp().$axios;
    const config = {
      method,
      url,
      ...axiosConfig,
    };
    if (['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
      config.data = payload;
    } else if (['GET', 'DELETE'].includes(method.toUpperCase())) {
      if (payload) {
        // Extract params separately and merge the rest of the config properties
        if (payload.params) {
          config.params = payload.params;
        }
        // Merge other axios config properties (like responseType, headers, etc.)
        const { params, ...restConfig } = payload;
        Object.assign(config, restConfig);
      }
    }

    try {
      const response = await axios(config);
      return response;
    } catch (error) {
      if (error?.response?.status !== 401) {
        console.error(error);
      }
      // Consider adding error handling logic here
      throw error; // Rethrow the error so it can be handled by the caller
    }
  };

  const service = {
    async get(url, payload) {
      return makeRequest(url, 'GET', payload);
    },
    async delete(url) {
      return makeRequest(url, 'DELETE');
    },
    async post(url, payload, axiosConfig = {}) {
      return makeRequest(url, 'POST', payload, axiosConfig);
    },
    async put(url, payload, axiosConfig = {}) {
      return makeRequest(url, 'PUT', payload, axiosConfig);
    },
    async patch(url, payload, axiosConfig = {}) {
      return makeRequest(url, 'PATCH', payload, axiosConfig);
    },
  };

  const $api = {
    // MFA endpoints
    async mfaStart(payload) {
      const token = payload?.token;
      const axiosConfig = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      return service.get('/mfa/setup', axiosConfig);
    },
    async mfaVerifySetup(payload) {
      const token = payload?.token;
      const body = { code: payload?.code };
      const axiosConfig = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      return service.post('/mfa/verify-setup', body, axiosConfig);
    },
    async mfaLoginVerify(payload) {
      const token = payload?.token;
      const axiosConfig = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const body = { email: payload?.email, code: payload?.code };
      return service.post('/mfa/verify-login', body, axiosConfig);
    },
    async mfaVerifyRecovery(payload) {
      const token = payload?.token;
      const body = { code: payload?.code };
      const axiosConfig = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      return service.post('/mfa/verify-recovery', body, axiosConfig);
    },
    async mfaReset() {
      return service.post('/mfa/reset');
    },
    get(url, params = null, respType = 'json') {
      return service.get(url, params, respType);
    },
    ...distributionsApi(service),
    async fetchRoles() {
      return this.get('/roles');
    },

    async getUser () {
      return this.get('/user');
    },

    async fetchAccounts () {
      return this.get('/accounts');
    },

    async fetchPostData (path) {
      return service.post(path);
    },

    async fetchAccount (path) {
      return this.get(`${path}`);
    },

    async createAccount (account) {
      return service.post('/accounts/create', account);
    },

    async editAccount (path, account) {
      return service.put(`/accounts/${path}`, account);
    },

    async deleteAccount (path) {
      return service.delete(`${path}`);
    },

    async addAccountUser (path, user) {
      return service.post(`${path}/add`, user);
    },

    async updateAccountUser (path, user) {
      return service.put(`${path}/${user.id}`, user);
    },

    async deleteAccountUser (path, user) {
      return service.delete(`${path}/${user.id}`, user);
    },

    async fetchProject (path) {
      return this.get(`${path}`);
    },

    async fetchProjectsByStatus (path, status) {
      return this.get(`${path}/projects/${status}`);
    },

    async fetchDraftProjects (path) {
      return this.get(`${path}/projects/draft`);
    },

    async fetchLiveProjects (path) {
      return this.get(`${path}/projects/live`);
    },

    async fetchPendingProjects (path) {
      return this.get(`${path}/projects/pending`);
    },

    async fetchClosedProjects (path) {
      return this.get(`${path}/projects/closed`);
    },

    async createProject (path, project) {
      return service.post(`/accounts/${path}/projects/create`, project);
    },

    async editProject (path, project) {
      return service.put(`${path}`, project);
    },

    async deleteProject (path) {
      return service.delete(`${path}`);
    },

    async fetchTask (path) {
      return this.get(path);
    },

    async fetchQuestionLayouts (path) {
      return this.get(`${path}/questions/layouts`);
    },

    async fetchQuestionTypes (path, type) {
      return this.get(`${path}/questions/types`);
    },

    async editTask (path, task) {
      return service.put(`${path}`, task);
    },

    async deleteTask (path) {
      return service.delete(path);
    },

    async fetchTaskExport (path) {
      return service({
        method: 'get',
        url: `${path}`,
        data: {},
        responseType: 'blob'
      });
    },

    async fetchTasksByStatus (path, status) {
      return this.get(`/${path}/tasks/${status}`);
    },

    async fetchDraftTasks (path) {
      return this.get(`/${path}/tasks/draft`);
    },

    async fetchLiveTasks (path) {
      return this.get(`/${path}/tasks/live`);
    },

    async fetchPendingTasks (path) {
      return this.get(`/${path}/tasks/pending`);
    },

    async fetchClosedTasks (path) {
      return this.get(`/${path}/tasks/closed`);
    },

    async createTask (path, task) {
      return service.post(`/accounts/${path}/tasks/create`, task);
    },

    async updateValue (path, payload) {
      return service.put(`${path}/update-field`, payload);
    },

    async updateQuestionValue (path, payload) {
      return service.put(`${path}/question/${payload.id}/update-field`, payload);
    },

    async fetchTaskGroups (path) {
      return service.get(`${path}/groups`);
    },

    async fetchTaskCoreQuestions (path) {
      return service.get(`/${path}/questions`);
    },

    async orderTaskQuestions (path, payload) {
      return service.put(`${path}/questions/reorder`, payload);
    },

    async addQuestion (path, payload, options) {
      return service.post(`${path}/questions/create`, payload, options);
    },

    async addSurveyQuestion (path, type, form, options) {
      return service.post(`${path}/survey/questions/create`, form, options);
    },

    async updateSurveyQuestion (path, type, questionId, form, options) {
      return service.post(`${path}/survey/questions/${questionId}`, form, options);
    },

    async addQuestionSegment (path, payload) {
      return service.post(`${path}/question/${payload.questionId}/segment`, payload);
    },

    async editQuestionSegment (path, payload) {
      return service.post(`${path}/question/${payload.questionId}/segment/${payload.id}`, payload);
    },

    async deleteQuestionSegment (path, payload) {
      return service.delete(`${path}/question/${payload.questionId}/segment/${payload.segmentId}`);
    },

    async updateQuestion (path, id, form, options) {
      return service.post(`${path}/question/${id}/update`, form, options);
    },

    async deleteQuestion (path, payload) {
      return service.delete(`${path}/question/${payload.id}`);
    },

    async addTaskGroup (path, payload) {
      return service.post(`${path}/groups/create`, payload);
    },

    async deleteTaskGroup (path, id) {
      return service.delete(`${path}/group/${id}`);
    },

    async updateGroup (path, payload) {
      return service.put(`${path}/groups/update-field`, payload);
    },

    async updateGroupValue (path, payload) {
      return service.put(`${path}/group/${payload.groupId}/update-field`, payload);
    },

    async syncGroupProfileGroups (path, payload) {
      return service.put(`${path}/group/${payload.groupId}/profile-groups`, {
        profile_group_ids: payload.profile_group_ids,
      });
    },

    async updateGroupQuestionValue (path, payload) {
      return service.put(`${path}/question/${payload.questionId}/update-field`, payload);
    },

    async updateSendInviteOnValue (path, payload) {
      return service.put(`${path}/group/${payload.groupId}/update-send-invite-on`, payload);
    },

    async setGroupsOrder (path, payload) {
      return service.put(`${path}/groups/reorder`, payload);
    },

    async editItem (path) {
      return service.post(path);
    },

    async deleteItem (path) {
      return service.post(path);
    },

    async addTaskUser (path, payload) {
      return service.post(`${path}/add`, payload);
    },

    async updateTaskUser (path, payload) {
      return service.put(`${path}/${payload.id}`, payload);
    },

    async deleteTaskUser (path, payload) {
      return service.post(`${path}/delete`, payload);
    },

    async taskSyncUser (path, payload) {
      return service.post(`${path}/${payload.id}/${payload.action}`);
    },

    async importTaskUsersFromCSV (path, rows) {
      return service.post(`${path}/users/import-csv`, rows);
    },

    async inviteTaskUser (path, payload) {
      return service.post(`${path}/${payload.id}/send-invite`, payload);
    },

    async sendTaskNextPartInvite (path, id) {
      return service.post(`${path}/${id}/send-next-part-invite`);
    },

    async completeTaskUser (path, id) {
      return service.post(`${path}/${id}/completed`);
    },

    async closeTaskUser (path, id) {
      return service.post(`${path}/${id}/close`);
    },

    async reopenTaskUser (path, id) {
      return service.post(`${path}/${id}/reopen`);
    },

    async payTaskUser (path, payload) {
      return service.post(`${path}/${payload.userId}/incentive/pay`, payload);
    },

    async fetchIncentiveTransactionsForUser (path, userId) {
      return service.get(`${path}/${userId}/incentive/transactions`);
    },

    async fetchIncentiveAvailableBalance (path, incentive) {
      return service.get(`incentives/${incentive}/balance`);
    },

    async cancelIncentivePayment (path, userId) {
      return service.post(`${path}/${userId}/incentive/cancel`);
    },

    async removeTaskUser (path, id) {
      return service.delete(`${path}/${id}/remove`);
    },

    /*
    Quotas
    -------------------
     */

    async getQuotas ({path}) {
      return service.get(`${path}/quotas`);
    },

    async updateQuotaValue ({path, payload}) {
      return service.put(`${path}/update-field`, payload);
    },

    async getQuotaProfileGroups(path) {
      return service.get(`${path}/settings/profile-group-categories`);
    },


    async createQuotaGroup(path, payload) {
      return service.post(`${path}/quotas`, payload);
    },

    async editQuotaGroup(path, data) {
      const {quotaGroupId, ...payload} = data;
      return service.put(`${path}/quotas/${quotaGroupId}`, payload);
    },

    async editQuotaSettings(path, payload) {
      return service.put(`${path}/quotas/`, payload);
    },

    async reorderQuotaGroups(path, payload) {
      return service.put(`${path}/quotas/reorder`, payload);
    },

    async deleteQuotaGroup(path, id) {
      return service.delete(`${path}/quotas/${id}`);
    },

    async createQuota(path, data) {
      const {quotaGroupId, ...payload} = data;
      return service.post(`${path}/quotas/${quotaGroupId}`, payload);
    },

    async editQuota(path, data) {
      const {quotaGroupId, quotaRowId, ...payload} = data;
      return service.put(`${path}/quotas/${quotaGroupId}/row/${quotaRowId}`, payload);
    },

    async deleteQuota(path, {quotaGroupId, quotaRowId}){
      return service.delete(`${path}/quotas/${quotaGroupId}/row/${quotaRowId}`);
    },

    async deleteAllQuotas({path}){
      return service.delete(`${path}/quotas/delete-all`);
    },

    async updateUserProfileWaitingListPreference({path, payload}){
      return service.post(path, payload);
    },

    async testCafeLogin(payload){
      return service.post("/login", payload);
    },

    // USERS (Global)

    async fetchUsers (path) {
      return service.get(path);
    },

    async syncUser (path, payload) {
      return service.post(path, payload);
    },

    // Authentication

    refreshToken() {
      return service.post('auth/refresh');
    },

    logout() {
      return service.post('auth/logout');
    },

    // Transcriptions

    async createTranscriptionProject (form) {
      return service.post(`/transcriptions/projects/create`, form);
    },

    async fetchTranscriptionProject (path) {
      return service.get(`/transcriptions/projects/${path}`);
    },

    deleteTranscriptionProject (path) {
      return service.delete(`/transcriptions/projects/${path}`);
    },

    deleteTranscriptionFile (path) {
      return service.delete(`/transcriptions/${path}`);
    },

    getTranscription (id) {
      return service.get(`/transcriptions/${id}`);
    },

    async transcribe (id, type) {
      return service.post(`/transcriptions/${id}/${type}/transcribe`);
    },

    async updateTranscript (payload) {
      return service.put(`/transcriptions/${payload.id}`, { 'payload': payload.transcript });
    },

    /////////////////

    async fetchTaskTypes () {
      return this.get('/task-types');
    },

    async fetchGlobalSetupValues () {
      return this.get('/global-setup-values');
    },

    async addTaskSegment(path, segment) {
      return service.post(`${path}/segments`, segment);
    },

    async updateTaskSegment(path, segment) {
      return service.put(`${path}/segments/${segment.id}`, segment);
    },

    async deleteTaskSegment(path, segment) {
      return service.delete(`${path}/segments/${segment.id}`);
    },

    async fetchProfileGroups(path) {
      return service.get(`${path}/settings/profile-groups`);
    },

    async fetchProfileGroupCategories(path) {
      return service.get(`${path}/settings/profile-group-categories`);
    },

    async createProfileGroupCategory(path, category) {
      return service.post(`${path}/settings/profile-group-categories`, category);
    },

    async editProfileGroupCategory(path, category) {
      return service.put(`${path}/settings/profile-group-categories/${category.id}`, category);
    },

    async addProfileGroup(path, group) {
      return service.post(`${path}/settings/profile-groups`, group);
    },

    async updateProfileGroup(path, group) {
      return service.put(`${path}/settings/profile-groups/${group.id}`, group);
    },

    async deleteProfileGroup(path, group) {
      return service.delete(`${path}/settings/profile-groups/${group.id}`);
    },

    async deleteProfileGroupCategory(path, category) {
      return service.delete(`${path}/settings/profile-group-categories/${category.id}`);
    },

    async fileUpload(slug, form, options) {
      return service.post(`${slug}`, form, options);
    },

    async sendDiscussionBoardMessage (path, payload, options) {
      return service.post(`${path}/discussion-board/send-message`, payload, options);
    },

    async getDiscussionBoardMessages (path, currentQuestion) {
      return service.get(`${path}/discussion-board/messages/${currentQuestion.id}`);
    },

    async getDiscussionBoardParticipants (path) {
      return service.get(`${path}/discussion-board/participants`);
    },

    async getDiscussionBoardQuestions (path) {
      return service.get(`${path}/discussion-board/questions`);
    },

    async getConversationProgress (path) {
      return service.get(`${path}/discussion-board/conversation-progress`);
    },

    async participantCompleteTask (path) {
      return service.put(`${path}`);
    },

    async fetchParticipantTasks () {
      return service.get(`/participant/tasks`);
    },

    async setUserOnlineStatus (path) {
      return service.put(`${path}`);
    },

    async sendMagicLink (payload) {
      return service.post(`/email-login`, payload);
    },

    async authenticateLoginToken (payload) {
      return service.post(`/email-login/authenticate`, payload);
    },

    async refreshLoginToken (payload) {
      return service.post(`/email-login/refresh`, payload);
    },

    async fetchTaskAnalysisByQuestion(path) {
      return service.get(`${path}`);
    },

    async fetchUsersByTaskSegments(path) {
      return service.get(`${path}/users/by-segment`);
    },

    async updateTaskUserSegment(path, payload) {
      return service.put(`${path}/users/${payload.user_id}/segment/update`, payload);
    },

    async fetchUsersByProfileGroups(path) {
      return service.get(`${path}/users/by-profile-group`);
    },

    async updateUserProfileGroup(path, payload) {
      return service.put(`${path}/users/${payload.user_id}/profile-group/update`, payload);
    },

    async addTaskDataParam(path, param) {
      return service.post(`${path}/url-data-params/`, param);
    },

    async updateTaskDataParam(path, param) {
      return service.put(`${path}/url-data-params/${param.id}`, param);
    },

    async deleteTaskDataParam(path, param) {
      return service.delete(`${path}/url-data-params/${param.id}`);
    },

    async fetchPageLogicGroups(path, groupId) {
      return service.get(`${path}/group/${groupId}/page-logic/groups`);
    },

    async fetchPageLogicQuestions(path, groupId) {
      return service.get(`${path}/group/${groupId}/page-logic/fetch-questions`);
    },

    async updatePageLogic(path, groupId, payload) {
      return service.post(`${path}/group/${groupId}/page-logic`, payload);
    },

    async getRefreshToken() {
      return service.post(`/auth/refresh`);
    },

      async axiosPatch(data) {
          const {path, payload, ...config} = data;
          return service.patch(`${path}`, payload, config);
      },

    async axiosGet({path, params, axiosConfig}) {
      return service.get(`${path}`, {params, ...axiosConfig});
    },

    async axiosPost(data) {
      const {path, payload, ...config} = data;
      return service.post(`${path}`, payload, config);
    },

    async axiosPut({path, payload}) {
      return service.put(`${path}`, payload);
    },

    async axiosDelete({path, payload}) {
      return service.delete(`${path}`, {data: payload});
    },

  };
  return $api;
}
